import { IMeetingRoomSettingsDays } from 'domain/feature/hub/company/meeting-room/settings/types'
import { AvailabilitySchedule, AvailabilityScheduleInput } from './types'

export const DEFAULT_OPEN_TIME = '08:00'
export const DEFAULT_CLOSE_TIME = '20:00'

export const DEFAULT_WORKING_DAYS: IMeetingRoomSettingsDays = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: true,
  sunday: true,
}

const DAY_INDEX_TO_KEY: Record<number, keyof IMeetingRoomSettingsDays> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
}

/** Monday-first weekday indexes (JS getDay). */
export const WEEKDAY_ORDER_MON_FIRST = [1, 2, 3, 4, 5, 6, 0] as const

export const parseTimeParts = (time: string): { hour: number; minute: number } => {
  const [hourRaw, minuteRaw] = time.split(':')
  const hour = Number(hourRaw)
  const minute = Number(minuteRaw)
  return {
    hour: Number.isFinite(hour) ? hour : 0,
    minute: Number.isFinite(minute) ? minute : 0,
  }
}

export const isWorkingDay = (
  date: Date,
  workingDays: IMeetingRoomSettingsDays
): boolean => {
  const key = DAY_INDEX_TO_KEY[date.getDay()]
  return key != null && workingDays[key]
}

export const getEnabledWeekdayIndexes = (
  workingDays: IMeetingRoomSettingsDays
): number[] =>
  WEEKDAY_ORDER_MON_FIRST.filter(dayIndex => {
    const key = DAY_INDEX_TO_KEY[dayIndex]
    return key != null && workingDays[key]
  })

export const resolveAvailabilitySchedule = (
  settings: AvailabilityScheduleInput | null
): AvailabilitySchedule => {
  const openTime = settings?.openTime?.trim() || DEFAULT_OPEN_TIME
  const closeTime = settings?.closeTime?.trim() || DEFAULT_CLOSE_TIME
  const workingDays = settings?.workingDays ?? DEFAULT_WORKING_DAYS

  const open = parseTimeParts(openTime)
  const close = parseTimeParts(closeTime)

  let hourStart = open.hour
  let hourEnd = close.minute > 0 ? close.hour + 1 : close.hour

  if (hourEnd <= hourStart) {
    hourStart = parseTimeParts(DEFAULT_OPEN_TIME).hour
    hourEnd = parseTimeParts(DEFAULT_CLOSE_TIME).hour
  }

  const enabledDays = getEnabledWeekdayIndexes(workingDays)
  const safeWorkingDays =
    enabledDays.length > 0 ? workingDays : DEFAULT_WORKING_DAYS

  return {
    openTime,
    closeTime,
    workingDays: safeWorkingDays,
    hourStart,
    hourEnd,
  }
}
